import { useMemo, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useLaunchParams, useSignal } from '@telegram-apps/sdk-react';
import {
  init,
  miniApp,
  themeParams,
  backButton,
  setMiniAppHeaderColor,
  setMiniAppBackgroundColor,
  retrieveLaunchParams,
  bindThemeParamsCssVars,
} from '@telegram-apps/sdk';
import { AppRoot } from '@telegram-apps/telegram-ui';

import { HomePage } from './pages/HomePage';
import { RequestPage } from './pages/RequestPage';
import { HistoryPage } from './pages/HistoryPage';
import { ReportPage } from './pages/ReportPage';
import { SettingsPage } from './pages/SettingsPage';
import { StatsPage } from './pages/StatsPage';
import { AdminPage } from './pages/AdminPage';
import { TabBar } from './components/layout';
import { useAdmin } from './hooks';

// Check if running inside Telegram
function isInTelegram(): boolean {
  try {
    retrieveLaunchParams();
    return true;
  } catch {
    return false;
  }
}

const IS_TELEGRAM = isInTelegram();

// Initialize SDK only in Telegram
if (IS_TELEGRAM) {
  try {
    init();
  } catch (e) {
    console.warn('Telegram SDK init failed:', e);
  }
}

// Component for Telegram environment
function TelegramApp() {
  const lp = useLaunchParams();
  // v3 SDK: state() - Record с snake_case ключами ('bg_color'), а не объект
  // с camelCase полями; isDark / цвета доступны как отдельные computed-сигналы
  const isDark = useSignal(themeParams.isDark);
  const bgColor = useSignal(themeParams.backgroundColor);
  const headerBgColor = useSignal(themeParams.headerBackgroundColor);

  // Инициализация строго один раз: bindThemeParamsCssVars при повторном
  // вызове бросает CSSVarsBoundError и роняет приложение в белый экран
  useEffect(() => {
    // Mount mini app
    if (miniApp.mount.isAvailable()) {
      miniApp.mount();
    }

    // Mount theme params
    if (themeParams.mount.isAvailable()) {
      themeParams.mount();
    }

    // Bind theme params to CSS variables with --tgui-- prefix for telegram-ui
    if (bindThemeParamsCssVars.isAvailable() && !themeParams.isCssVarsBound()) {
      bindThemeParamsCssVars((key) => `--tgui--${key}`);
    }

    // Mount back button
    if (backButton.mount.isAvailable()) {
      backButton.mount();
    }

    // Signal ready
    if (miniApp.ready.isAvailable()) {
      miniApp.ready();
    }
  }, []);

  // Цвета шапки/фона обновляем при каждой смене темы
  useEffect(() => {
    if (setMiniAppHeaderColor.isAvailable() && headerBgColor) {
      setMiniAppHeaderColor(headerBgColor);
    }
    if (setMiniAppBackgroundColor.isAvailable() && bgColor) {
      setMiniAppBackgroundColor(bgColor);
    }
  }, [headerBgColor, bgColor]);

  const platform = String(lp?.platform ?? 'base');

  return (
    <AppRoot
      appearance={isDark ? 'dark' : 'light'}
      platform={['macos', 'ios'].includes(platform) ? 'ios' : 'base'}
    >
      <AppRoutes />
    </AppRoot>
  );
}

// Component for browser development
function BrowserApp() {
  const [isDark, setIsDark] = useState(() =>
    window.matchMedia('(prefers-color-scheme: dark)').matches
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setIsDark(e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  return (
    <AppRoot appearance={isDark ? 'dark' : 'light'} platform="base">
      <div style={{ padding: '8px', background: '#fff3cd', color: '#856404', fontSize: '12px', textAlign: 'center' }}>
        Dev Mode: Open in Telegram for full experience
      </div>
      <AppRoutes />
    </AppRoot>
  );
}

// Pages that show TabBar
const TABBAR_PATHS = ['/', '/stats', '/settings', '/admin'];

function shouldShowTabBar(pathname: string): boolean {
  return TABBAR_PATHS.includes(pathname);
}

// Layout with conditional TabBar
function AppLayout() {
  const location = useLocation();
  const { isAdmin } = useAdmin();
  const showTabBar = shouldShowTabBar(location.pathname);

  return (
    <>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/request/:id" element={<RequestPage />} />
        <Route path="/request/:id/history" element={<HistoryPage />} />
        <Route path="/report" element={<ReportPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/stats" element={<StatsPage />} />
        <Route path="/admin" element={<AdminPage />} />
      </Routes>
      {showTabBar && <TabBar isAdmin={isAdmin} />}
    </>
  );
}

// Shared routes
function AppRoutes() {
  return (
    <BrowserRouter>
      <AppLayout />
    </BrowserRouter>
  );
}

function AppInner() {
  return IS_TELEGRAM ? <TelegramApp /> : <BrowserApp />;
}

export function App() {
  const queryClient = useMemo(() => new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30000,
        retry: 2,
      },
    },
  }), []);

  return (
    <QueryClientProvider client={queryClient}>
      <AppInner />
    </QueryClientProvider>
  );
}
